import { CompositionChildEditPreviewDto } from '@app/modules/compose/composition.types';
import { Box } from '@mui/material';

import { COMPOSITION_CARD_EDIT_STRIP_HEIGHT_PX } from './composition-card-edit-strip-constants';
import { getCompositionEditThumbnailSrc } from './get-composition-edit-thumbnail-src';

export interface CompositionCardEditStripItemProps {
  edit: CompositionChildEditPreviewDto;
  /** Whether this item is the edit currently shown in the main preview. */
  selected: boolean;
  onMouseEnter: () => void;
}

/**
 * One edit thumbnail in the composition card strip. Height is fixed; width follows
 * the image's intrinsic aspect ratio (API does not expose aspect ratio yet).
 * Renders nothing when the edit has no thumbnail URL.
 */
export function CompositionCardEditStripItem({
  edit,
  selected,
  onMouseEnter,
}: CompositionCardEditStripItemProps) {
  const src = getCompositionEditThumbnailSrc(edit);
  if (!src) {
    return null;
  }

  const height = COMPOSITION_CARD_EDIT_STRIP_HEIGHT_PX;

  return (
    <Box
      onMouseEnter={onMouseEnter}
      sx={{
        position: 'relative',
        flex: '0 0 auto',
        height,
        borderRadius: 0.5,
        overflow: 'hidden',
        bgcolor: (theme) =>
          theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
        outline: selected ? (theme) => `2px solid ${theme.palette.primary.main}` : 'none',
        outlineOffset: -2,
        boxSizing: 'border-box',
        // Dim siblings so the edit driving the main preview stays visually primary.
        opacity: selected ? 1 : 0.55,
        transition: (theme) =>
          theme.transitions.create('opacity', {
            duration: theme.transitions.duration.shorter,
          }),
      }}
    >
      <Box
        component="img"
        src={src}
        alt=""
        sx={{
          display: 'block',
          height: '100%',
          width: 'auto',
          maxWidth: 'none',
        }}
      />
    </Box>
  );
}
